import dayjs from "dayjs";
import {
  PrivateTimeForm,
  constants,
  useRequestLinesStore,
} from "entities/TravelLines";
import { getTravelLineResolvers } from "features/EditRequestLines/lib/validations/getTravelLineResolvers";
import {
  forwardRef,
  memo,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import { useForm } from "react-hook-form";
import api, { baseApiService } from "shared/api";
import { ROLES } from "shared/config";
import { withToast } from "shared/utils";

const EditPrivateTime = forwardRef(
  (
    // @ts-ignore
    { travelLineData, traveLineIndex, traveLineId, disabled },
    ref
  ) => {
    const { defaultValues } = constants.travelLines;

    const { privateTimeOnSaveResolver, privateTimeOnSubmitResolver } =
      getTravelLineResolvers();

    const travelPlanSchema = useRequestLinesStore(
      (state) => state.travelPlanSchema
    );
    const addRequestLineToStack = useRequestLinesStore(
      (state) => state.addRequestLineToStack
    );
    const removeTravelLine = useRequestLinesStore(
      (state) => state.removeTravelLine
    );
    const deleteRequestLineState = useRequestLinesStore(
      (state) => state.deleteRequestLineState
    );
    const updateRequestLinesState = useRequestLinesStore(
      (state) => state.updateRequestLinesState
    );

    const resolver = useRef(privateTimeOnSaveResolver);

    const form = useForm({
      resolver: (...args) => resolver.current(...args),
      defaultValues,
    });

    const {
      handleSubmit,
      setValue,
      reset,
      formState: { isDirty },
    } = form;

    useEffect(() => {
      const { startPlace } = travelPlanSchema[traveLineIndex + 1] ?? {};
      const { endPlace } = travelPlanSchema[traveLineIndex - 1] ?? {};
      if (endPlace) {
        setValue("start_place_id", endPlace);
      }
      if (startPlace) {
        setValue("end_place_id", startPlace);
      }
    }, [travelPlanSchema]);

    const resetForm = () => {
      reset({
        ...Object.fromEntries(
          Object.entries(defaultValues).map(([key, value]) => {
            let newValue = travelLineData?.[key] ?? value;
            if (
              key === "start_time" ||
              key === "end_time" ||
              key === "border_crossing_time"
            ) {
              newValue = typeof newValue === "string" ? dayjs(newValue) : null;
            } else if (key === "attachments") {
              newValue = travelLineData?.[key]?.data?.map(
                (fileData) => fileData.id
              );
            }

            return [key, newValue];
          })
        ),
        id: traveLineId.toString().startsWith("initial") ? null : traveLineId,
      });
    };

    useEffect(() => {
      updateRequestLinesState(traveLineId, {
        isModified: isDirty || traveLineId.toString().startsWith("initial"),
      });
    }, [isDirty]);

    useEffect(() => {
      setValue("order", traveLineIndex, { shouldDirty: true });
    }, [traveLineIndex]);

    useEffect(() => {
      resetForm();
    }, [travelLineData]);

    const handleRequestLineSave = (isDirty, traveLineId) => {
      addRequestLineToStack(
        new Promise((resolve, reject) => {
          handleSubmit(
            (formData) =>
              resolve({
                isValid: true,
                formData: {
                  ...formData,
                  interval_type_id: formData.interval_type_id?.value,
                  vehicle_info: formData.vehicle_info?.value,
                  lineType: "travel",
                  isDirty:
                    isDirty || traveLineId.toString().startsWith("initial"),
                },
              }),
            (errors) => resolve({ isValid: false, errors })
          )();
        })
      );
    };

    const handleRequestLineDelete = async (traveLineId, requestId) => {
      if (traveLineId.toString().startsWith("initial")) {
        removeTravelLine(traveLineId);
        deleteRequestLineState(traveLineId);
      } else {
        updateRequestLinesState(traveLineId, { isDeleting: true });
        const response = await withToast(() =>
          api.post(baseApiService.ENDPOINTS.travel_line.crud, {
            user_role: ROLES.USER,
            application_id: requestId,
            travel_intervals: [
              {
                id: traveLineId,
                delete: true,
              },
            ],
          })
        );
        if (response?.status === 200) {
          deleteRequestLineState(traveLineId);
          removeTravelLine(traveLineId);
        }
      }
    };

    useImperativeHandle(ref, () => ({
      save() {
        resolver.current = privateTimeOnSaveResolver;
        handleRequestLineSave(isDirty, traveLineId);
      },
      submit() {
        resolver.current = privateTimeOnSubmitResolver;
        handleRequestLineSave(isDirty, traveLineId);
      },
      delete(requestId) {
        handleRequestLineDelete(traveLineId, requestId);
      },
    }));

    return (
      <PrivateTimeForm
        form={form}
        handleSubmit={handleSubmit}
        disabled={disabled}
      />
    );
  }
);

export default memo(EditPrivateTime);
